import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAnalyticsPageView, usePathParam } from '@chilecompra/react-kit'
import { Card, Skeleton, Spinner, Table, Toast, Typography } from '@chilecompra/react-kit/components'
import { useAuthorization } from '../../containers/AuthorizationProvider/AuthorizationProvider.hooks'
import { onGetSellerLegalDocumentsThunk, onDownloadLegalDocumentsThunk } from './LegalDocumentationSection.actions'
import { Box, Content, Link, TableCell, TableRow } from './LegalDocumentationSection.styles'

/**
 * The LegalDocumentationSection's container.
 *
 */
const LegalDocumentationSection = () => {
  const dispatch = useDispatch()
  const dni = usePathParam('rut')
  const { userType } = useAuthorization()
  const { currentDownload, documents, error, loading } = useSelector(state => state.legalDocuments)
  const { contractStatus, loading: contractStatusLoading } = useSelector(state => state.basicSellerInformation)

  const handleDownloadFile = (code, fileName) => () => dispatch(onDownloadLegalDocumentsThunk({ code, fileName }))
  useAnalyticsPageView('/ficha/documentos')

  useEffect(() => {
    if (!loading) {
      return
    }
    if (contractStatus === 1) {
      dispatch(onGetSellerLegalDocumentsThunk({ dni, userType }))
    }
  }, [contractStatus])

  if (contractStatusLoading) return null
  if (contractStatus !== 1) return null

  return (
    <Content>
      <Card color="blank" enableBorder padding="32px 24px 8px">
        <Typography variant="h4" fontWeight="bold" padding="0 0 3px">
          Documentos legales
        </Typography>

        {!loading && !error && (
          <Box>
            <Box margin="12px 0">
              <Typography variant="body2">
                El Registro de Proveedores opera como un repositorio de documentos. Es responsabilidad del comprador, a
                la hora de contratar, revisar la validez y legalidad de los documentos presentados por los proveedores
                que contrata.
              </Typography>
            </Box>
          </Box>
        )}

        {!loading && error && (
          <Toast
            disableBackground
            margin="1em"
            severity="default"
            title="Lo sentimos, estamos presentando problemas con nuestro servidor."
          >
            En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos minutos
            más.
          </Toast>
        )}

        {!loading && !error && documents?.length === 0 && (
          <Typography color="black3" margin="12px 0 41.5px" variant="body2">
            Este proveedor no ha ingresado documentos legales.
          </Typography>
        )}

        {loading && !error && <Skeleton height="76.65px" margin="8px 0" variant="rect" width="100%" />}

        {!loading && !error && documents?.length > 0 && (
          <Box margin="12px 0 32px">
            <Table accessibility={{ label: 'Documentos legales' }} borderCollapse="separate" borderSpacing="0 8px">
              {documents.map(document => (
                <TableRow backgroundColor="primaryLight" key={document.folio}>
                  {!document.belongsToEE1D ? (
                    <>
                      <TableCell disableBottomBorder padding="4px 0 0 42px" width="50%">
                        {currentDownload[document.folio] ? (
                          <Spinner size={20} />
                        ) : (
                          <Link onClick={handleDownloadFile(document.folio, document.name)} target="_blank">
                            {document.name}
                          </Link>
                        )}
                        <Typography margin="5px 0" color="black1" variant="subtitle">
                          {document.description}
                        </Typography>
                      </TableCell>
                      <TableCell disableBottomBorder padding="20px 0">
                        <Typography fontWeight="bold" variant="caption">
                          Fecha de carga
                        </Typography>
                        <Typography margin="5px 0" variant="body2">
                          {document.uploadedDate}
                        </Typography>
                      </TableCell>
                      <TableCell disableBottomBorder padding="20px 0">
                        <Typography fontWeight="bold" variant="caption">
                          Fecha de vigencia
                        </Typography>
                        <Typography margin="5px 0" variant="body2">
                          {document.validityDate}
                        </Typography>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell disableBottomBorder padding="4px 0 0 42px" width="50%">
                        <Typography variant="body2" padding="18px 0 0 0">
                          {document.description}
                        </Typography>
                        <Typography margin="5px 0" color="black3" variant="subtitle" padding="0 0 18px 0">
                          Fuente: Registro de Empresas y Sociedades
                        </Typography>
                      </TableCell>
                      <TableCell disableBottomBorder colSpan="2">
                        <Link fontSize="14px" href={document.url} target="_blank" rel="noreferrer">
                          Generar certificado desde Empresa en Un Día
                        </Link>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </Table>
          </Box>
        )}
      </Card>
    </Content>
  )
}

export default LegalDocumentationSection
